import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [fullName, setFullName] = useState(null);
    const [email, setEmail] = useState(null);
    const [names, setNames] = useState({});          // username -> full name, for every account
    const [mustResetPassword, setMustResetPassword] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuth();
    }, []);

    // Every page shows people, not logins, wherever a row records who did something.
    const loadNames = async (token) => {
        try {
            const r = await fetch('/api/users/names', { headers: { 'Authorization': `Bearer ${token}` } });
            if (r.ok) setNames(await r.json());
        } catch (e) { /* names stay as usernames */ }
    };
    const nameOf = (username) => (username && names[username]) || username;

    const checkAuth = async () => {
        const token = localStorage.getItem('access_token');
        if (token) {
            try {
                // /api/auth/me returns {username, full_name, permissions,
                // must_reset_password} in one call — used (rather than
                // /api/auth/verify) so permission-gated nav knows what to render.
                const response = await fetch('/api/auth/me', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setUser(data.username || 'authenticated');
                    setFullName(data.full_name || null);
                    setEmail(data.email || null);
                    setPermissions(data.permissions || []);
                    setMustResetPassword(!!data.must_reset_password);
                    loadNames(token);
                } else {
                    localStorage.removeItem('access_token');
                    setUser(null);
                    setPermissions([]);
                }
            } catch (error) {
                console.error('Auth check failed:', error);
                localStorage.removeItem('access_token');
                setUser(null);
                setPermissions([]);
            }
        }
        setLoading(false);
    };

    const login = async (username, password) => {
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
            });

            if (response.ok) {
                const data = await response.json();
                localStorage.setItem('access_token', data.access_token);
                setUser(username.trim().toLowerCase());
                setMustResetPassword(!!data.must_reset_password);

                // Fetch permissions right away so gated nav shows up without
                // needing a page reload.
                try {
                    const meResp = await fetch('/api/auth/me', {
                        headers: { 'Authorization': `Bearer ${data.access_token}` },
                    });
                    if (meResp.ok) {
                        const me = await meResp.json();
                        setFullName(me.full_name || null);
                        setEmail(me.email || null);
                        setPermissions(me.permissions || []);
                    }
                    loadNames(data.access_token);
                } catch (e) {
                    console.error('Failed to fetch permissions after login:', e);
                }

                return { success: true, user: username.trim().toLowerCase(), mustResetPassword: !!data.must_reset_password };
            } else {
                const errorData = await response.json();
                return { success: false, error: errorData.detail || 'Login failed' };
            }
        } catch (error) {
            console.error('❌ Login error:', error);
            return { success: false, error: 'Network error' };
        }
    };

    const logout = () => {
        // Let the server note the sign-out; the token is cleared either way.
        const token = localStorage.getItem('access_token');
        if (token) fetch('/api/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        localStorage.removeItem('access_token');
        setUser(null);
        setPermissions([]);
        setFullName(null);
        setEmail(null);
        setNames({});
        setMustResetPassword(false);
    };

    return (
        <AuthContext.Provider value={{
            user, permissions, fullName, email, mustResetPassword, setMustResetPassword,
            nameOf, login, logout, loading,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

// Public — no auth required, used by the forgot/reset password flow.
export const requestPasswordReset = async (email) => {
    const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.detail || 'Something went wrong');
    return data;
};

export const confirmPasswordReset = async (token, newPassword) => {
    const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.detail || 'Something went wrong');
    return data;
};

export const LoginForm = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (user) {
            const from = location.state?.from?.pathname || '/';
            navigate(from, { replace: true });
        }
    }, [user, navigate, location]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        const result = await login(username, password);
        if (!result.success) {
            setError(result.error || 'Login failed');
            setIsLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f6f6f7',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
            {/* Shopify-style header */}
            <div style={{ marginBottom: 32, textAlign: 'center' }}>
                <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    backgroundColor: '#202223',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 16px',
                    fontSize: 22,
                }}>
                    ⚽
                </div>
                <h1 style={{
                    fontSize: 20, fontWeight: 700, color: '#202223',
                    margin: '0 0 4px', letterSpacing: '-0.02em',
                }}>
                    Soccer Post Utility Hub
                </h1>
                <p style={{ color: '#6d7175', fontSize: 14, margin: 0 }}>
                    Sign in to your account
                </p>
            </div>

            {/* Card */}
            <div style={{
                width: '100%', maxWidth: 380,
                backgroundColor: '#ffffff',
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06)',
                padding: '28px 28px 24px',
                margin: '0 16px',
            }}>
                <form onSubmit={handleSubmit}>
                    {/* Username */}
                    <div style={{ marginBottom: 16 }}>
                        <label style={{
                            display: 'block', marginBottom: 6,
                            fontSize: 13, fontWeight: 550, color: '#202223',
                        }}>
                            Username
                        </label>
                        <input
                            type="text"
                            required
                            autoComplete="username"
                            autoFocus
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            disabled={isLoading}
                            placeholder="Enter your username"
                            style={{
                                width: '100%', boxSizing: 'border-box',
                                padding: '8px 12px',
                                border: '1px solid #c9cccf',
                                borderRadius: 8, fontSize: 14,
                                color: '#202223', outline: 'none',
                                backgroundColor: isLoading ? '#f6f6f7' : '#ffffff',
                                transition: 'border-color 0.15s, box-shadow 0.15s',
                            }}
                            onFocus={(e) => { e.target.style.borderColor = '#008060'; e.target.style.boxShadow = '0 0 0 2px rgba(0,128,96,0.2)'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#c9cccf'; e.target.style.boxShadow = 'none'; }}
                        />
                    </div>

                    {/* Password */}
                    <div style={{ marginBottom: 20 }}>
                        <label style={{
                            display: 'block', marginBottom: 6,
                            fontSize: 13, fontWeight: 550, color: '#202223',
                        }}>
                            Password
                        </label>
                        <input
                            type="password"
                            required
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={isLoading}
                            placeholder="Enter your password"
                            style={{
                                width: '100%', boxSizing: 'border-box',
                                padding: '8px 12px',
                                border: '1px solid #c9cccf',
                                borderRadius: 8, fontSize: 14,
                                color: '#202223', outline: 'none',
                                backgroundColor: isLoading ? '#f6f6f7' : '#ffffff',
                                transition: 'border-color 0.15s, box-shadow 0.15s',
                            }}
                            onFocus={(e) => { e.target.style.borderColor = '#008060'; e.target.style.boxShadow = '0 0 0 2px rgba(0,128,96,0.2)'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#c9cccf'; e.target.style.boxShadow = 'none'; }}
                        />
                    </div>

                    {/* Error */}
                    {error && (
                        <div style={{
                            marginBottom: 16, padding: '10px 12px',
                            backgroundColor: '#fff4f4',
                            border: '1px solid #ffd2d2',
                            borderRadius: 8, color: '#d72c0d',
                            fontSize: 13,
                        }}>
                            {error}
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                            width: '100%', padding: '9px 16px',
                            backgroundColor: isLoading ? '#95c9b4' : '#008060',
                            color: '#ffffff', border: 'none',
                            borderRadius: 8, fontSize: 14, fontWeight: 600,
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.15s',
                            letterSpacing: '0.01em',
                        }}
                        onMouseEnter={(e) => { if (!isLoading) e.target.style.backgroundColor = '#006e52'; }}
                        onMouseLeave={(e) => { if (!isLoading) e.target.style.backgroundColor = '#008060'; }}
                    >
                        {isLoading ? 'Signing in...' : 'Sign in'}
                    </button>
                </form>
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <a
                        href="/forgot-password"
                        onClick={(e) => { e.preventDefault(); navigate('/forgot-password'); }}
                        style={{ color: '#008060', fontSize: 13, textDecoration: 'none' }}
                    >
                        Forgot your password?
                    </a>
                </div>
                {/* The shared `admin` account was retired on 2026-10-05. Anyone still
                    reaching for it needs their own account, so point them at IT here
                    rather than leaving them on a bare "incorrect password". */}
                <div style={{
                    marginTop: 16, paddingTop: 16,
                    borderTop: '1px solid #e1e3e5',
                    textAlign: 'center',
                    fontSize: 12.5, lineHeight: 1.5, color: '#6d7175',
                }}>
                    Using the <strong style={{ fontWeight: 600, color: '#202223' }}>admin</strong> account?
                    It has been retired — email{' '}
                    <a
                        href="mailto:tech@soccerpost.com"
                        style={{ color: '#008060', textDecoration: 'none', whiteSpace: 'nowrap' }}
                    >
                        tech@soccerpost.com
                    </a>{' '}
                    for your own credentials.
                </div>
            </div>
        </div>
    );
};

export const ProtectedRoute = ({ children }) => {
    const { user, mustResetPassword, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                color: '#6b7280'
            }}>
                Loading...
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Blocks everything else until a temporary/reset password is replaced —
    // the set-new-password route itself is still wrapped in ProtectedRoute
    // (must be logged in), so this check must not loop on its own path.
    if (mustResetPassword && location.pathname !== '/set-new-password') {
        return <Navigate to="/set-new-password" replace />;
    }

    return children;
};

export const authenticatedFetch = async (url, options = {}) => {
    const token = localStorage.getItem('access_token');
    
    const headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`
    };

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        throw new Error('Authentication required');
    }

    return response;
};

export const debugAuth = () => {
    const token = localStorage.getItem('access_token');
    console.log('🔍 Debug Auth State:');
    console.log('Token exists:', !!token);
    
    if (token) {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            console.log('Token payload:', payload);
            console.log('Expires:', new Date(payload.exp * 1000));
            console.log('Is expired:', Date.now() > payload.exp * 1000);
        } catch (e) {
            console.error('Invalid token format');
        }
    }
};
